import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getIsStorm } from '@liquidweb-dev/banana-stand/parsers';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import {
	LWApiPublicAssetDetailsParamsRawI,
	LWApiPublicAssetDetailsResultRawI,
	LWApiPublicAssetFeatureDetailsRawT,
	ResourceStateRawI,
} from '@liquidweb-dev/club-sauce/public/asset/index.raw';
import { type ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import { dataCenterMap } from 'modules/regions';
import api from 'modules/queries/api';
import displayName from 'utility/assetDisplayNames';
import { getType as getBackupType } from 'modules/backups/helpers';
import createAssetType from 'utility/createAssetType';
import formatBytes from 'utility/formatBytes';
import isNutanixPrivateCloud, {
	isNutanixVm,
	isPrismManaged,
} from 'utility/nutanixProductCodes';
import { getBeyondHostingPowerStatus, isUnknown } from 'utility/powerStatus';

/** TODO: drop once club-sauce types `backupsAndStorage.acronis_edr` (absent when the server has no EDR). */
export type AssetDetailsResultRaw = Omit<
	LWApiPublicAssetDetailsResultRawI,
	'backupsAndStorage'
> & {
	backupsAndStorage?: LWApiPublicAssetDetailsResultRawI['backupsAndStorage'] & {
		acronis_edr?: {
			status: string;
			uniq_id: string;
		};
	};
};

/** The Acronis EDR subaccount statuses that mean a server has EDR. */
export const ACRONIS_EDR_PRESENT_STATUSES = [
	'Pending-Activation',
	'Active',
	'Suspended',
];

/** An EDR server is permanently on the Acronis Cloud tenant, so it can only back up to Acronis Cloud. */
export const getHasAcronisEdr = (
	backupsAndStorage: AssetDetailsResultRaw['backupsAndStorage'],
) =>
	ACRONIS_EDR_PRESENT_STATUSES.includes(
		backupsAndStorage?.acronis_edr?.status ?? '',
	);

interface BackupsState {
	canAcronis: boolean;
	canCloudBackup: boolean;
	currentPlan: 'Acronis' | 'Daily' | 'CloudDaily' | 'Quota' | 'None';
	hasAcronis: boolean;
	hasAcronisEdr: boolean;
	hasBackups: boolean;
	hasLegacyBackups: boolean;
}

interface ExtendedAssetDetails {
	acronisId?: string;
	acronisStatus?: string;
	acronisEdrId?: string;
	acronisEdrStatus?: string;
	assetType: string;
	/** prefer this to zone.id if referencing templates, plans, etc. that have are only available in a specific zones */
	availabilityZoneId: number | null;
	backupPlan?: 'Acronis' | 'Daily' | 'CloudDaily' | 'Quota' | 'None';
	backupQuotaValue?: string;
	backupsState?: BackupsState;
	bandwidthInGB?: number;
	canBackups?: boolean;
	canConsole?: boolean;
	canImage?: boolean;
	controlPanel?: string;
	country?: string;
	cores?: number | null;
	cpanelLicense?: string;
	cpu?: string;
	disableConsole?: boolean;
	displayType?: string;
	gpu?: string;
	mrc?: { base_price_mrc: string; base_price_mrc_pre_discount: string };
	hardDrives?: { title: string; description: string }[];
	hasAcronis: boolean;
	hasBlockStorage?: boolean;
	isBareMetal?: boolean;
	isBeyondHosting?: boolean;
	isMWPv1?: boolean;
	isNutanixPrivateCloud?: boolean;
	isNutanixVm?: boolean;
	isOpenStack?: boolean;
	isPrismManaged?: boolean;
	isPrivateParent?: boolean;
	isStorm?: boolean;
	isVmware?: boolean;
	isWindows?: boolean;
	linkSpeed?: string;
	location?: string;
	managedType?: string;
	managementLevel?: string;
	mesRole?: string;
	mesType?: string;
	os?: string;
	osFormatted?: string;
	parentDiskResources?: { free: number; used: number; total: number } | null;
	parentMemoryResources?: ResourceStateRawI | null;
	path: string;
	powerStatus?: string;
	primaryStorage?: string;
	privateIp?: string | null;
	processor?: string;
	processorSpeed?: string;
	raidLevelFormatted?: string;
	ram?: string | number;
	ramFormatted?: string;
	zoneFormatted?: string;
}

export type AssetDetails = Omit<AssetDetailsResultRaw, 'powerStatus'> &
	ExtendedAssetDetails;

/**
 * removes square backets and contents from osName (for example:
 * [App:WordPress Optimized])
 */
export const stripOsName = (osName: string) => {
	return osName.replace(/\s*\[.*?\]\s*/g, '');
};

const getOS = ({
	assetType,
	featureDetails,
	nocworxRemoteDetails,
	isBeyondHosting,
	machine,
}: {
	assetType: string;
	featureDetails?: LWApiPublicAssetFeatureDetailsRawT;
	nocworxRemoteDetails: LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails'];
	isBeyondHosting?: boolean;
	machine: LWApiPublicAssetDetailsResultRawI['machine'];
}) => {
	if (isBeyondHosting) {
		return nocworxRemoteDetails?.os?.identity || '';
	}

	if (!featureDetails) {
		return '';
	}

	if (assetType === 'open stack') {
		return (
			Object.values(featureDetails)?.find((option) =>
				fromApiBoolean(option.capabilities.nocworxImage),
			)?.description || ''
		);
	}

	if (assetType === 'bare metal') {
		return machine?.template_description || '';
	}

	return (
		featureDetails.Template?.description ||
		featureDetails.centos?.description ||
		featureDetails.winos?.description ||
		featureDetails.OS?.description ||
		''
	);
};

const getProcessor = ({
	assetType,
	featureDetails,
	machine,
}: {
	assetType: string;
	featureDetails?: LWApiPublicAssetFeatureDetailsRawT;
	machine: LWApiPublicAssetDetailsResultRawI['machine'];
}) => {
	if (assetType === 'open stack') {
		return Object.values(featureDetails || {}).find((option) =>
			fromApiBoolean(option.capabilities.nocworxCPU),
		)?.description;
	}

	if (machine?.cpu_model) {
		const { cpu_model: cpuModel, cpu_count: cpuCount } = machine;

		if (cpuCount === 2) return `Dual ${cpuModel}`;
		if (cpuCount === 4) return `Quad ${cpuModel}`;

		return cpuModel;
	}

	return (
		featureDetails?.ConfigId?.cpu_description ||
		featureDetails?.Processor?.description
	);
};

const getCores = ({
	featureDetails,
	isPrivateParent,
	nocworxRemoteDetails,
	machine,
}: {
	featureDetails?: LWApiPublicAssetFeatureDetailsRawT;
	isPrivateParent: boolean;
	nocworxRemoteDetails: LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails'];
	machine: LWApiPublicAssetDetailsResultRawI['machine'];
}) => {
	if (isPrivateParent) {
		const { cpu_cores_per_socket: cores = 0, cpu_sockets: sockets = 0 } =
			featureDetails?.ConfigId || {};
		const displayedCores = cores * sockets;

		return displayedCores;
	}

	return (
		machine?.cpu_cores ||
		nocworxRemoteDetails?.server_details?.cpu ||
		featureDetails?.ConfigId?.vcpu ||
		featureDetails?.ConfigId?.cpu_cores ||
		featureDetails?.vDCvCPU?.num_units
	);
};

/**
 * Depending on the asset, returns the processor, cores, or vCPUs.
 */
const getCpu = ({
	assetType,
	featureDetails,
	isBeyondHosting,
	isChild,
	isPrivateParent,
	isVmware,
	nocworxRemoteDetails,
	machine,
}: {
	assetType: string;
	featureDetails?: LWApiPublicAssetFeatureDetailsRawT;
	isBeyondHosting?: boolean;
	isChild: boolean;
	isPrivateParent: boolean;
	isVmware: boolean;
	nocworxRemoteDetails?: LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails'];
	machine: LWApiPublicAssetDetailsResultRawI['machine'];
}) => {
	if (isBeyondHosting || isChild || isPrivateParent || assetType === 'cloud') {
		const cores = getCores({
			featureDetails,
			isPrivateParent,
			nocworxRemoteDetails,
			machine,
		});

		if (!cores) return '';

		return cores + (cores > 1 ? ' cores' : ' core');
	}

	if (
		assetType === 'open stack' ||
		assetType === 'dedicated' ||
		assetType === 'bare-metal'
	) {
		return getProcessor({ assetType, featureDetails, machine });
	}

	if (isVmware) {
		const vCpus = featureDetails?.vDCvCPU?.num_units;

		return vCpus ? `${vCpus} vCPU's` : '';
	}

	return '';
};

const getGpu = ({
	assetType,
	machine,
}: {
	assetType: string;
	machine: LWApiPublicAssetDetailsResultRawI['machine'];
}) => {
	if (assetType === 'bare metal') {
		if (!machine) return '';

		const { gpu_name: gpuName, gpu_count: gpuCount } = machine;

		if (!gpuName) return '';

		if (typeof gpuCount === 'number' && gpuCount > 1) {
			return `${gpuCount} x ${gpuName}`;
		}

		return gpuName;
	}

	return '';
};

const getRam = ({
	assetType,
	featureDetails,
	isBeyondHosting,
	isPrivateParent,
	isChild,
	nocworxRemoteDetails,
	machine,
}: {
	assetType: string;
	featureDetails?: LWApiPublicAssetFeatureDetailsRawT;
	isBeyondHosting?: boolean;
	isPrivateParent: boolean;
	isChild: boolean;
	nocworxRemoteDetails?: LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails'];
	machine: LWApiPublicAssetDetailsResultRawI['machine'];
}) => {
	if (isBeyondHosting && nocworxRemoteDetails?.server_details?.ram) {
		return `${nocworxRemoteDetails?.server_details?.ram}MB`;
	}

	if (assetType === 'bare metal') {
		return machine?.memory ? `${machine.memory} GB` : '';
	}

	if (!featureDetails) {
		return '';
	}

	if (isPrivateParent || isChild || assetType === 'cloud') {
		return featureDetails.ConfigId?.memory;
	}

	if (assetType === 'open stack') {
		return (
			Object.values(featureDetails).find((option) =>
				fromApiBoolean(option.capabilities.nocworxMemory),
			)?.description || ''
		);
	}

	if (assetType === 'dedicated') {
		return featureDetails.RAM?.description || '';
	}

	return '';
};

const getRamFormatted = (ram: AssetDetails['ram']) => {
	if (!ram) {
		return '';
	}

	if (typeof ram === 'number') {
		return formatBytes(ram * 1e6).withUnit;
	}

	return ram;
};

const getPrimaryStorage = ({
	assetType,
	featureDetails,
	isBeyondHosting,
	nocworxRemoteDetails,
	machine,
}: {
	assetType: string;
	featureDetails?: LWApiPublicAssetFeatureDetailsRawT;
	isBeyondHosting?: boolean;
	nocworxRemoteDetails?: LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails'];
	machine?: LWApiPublicAssetDetailsResultRawI['machine'];
}) => {
	if (isBeyondHosting) {
		if (!nocworxRemoteDetails?.server_details) {
			return '';
		}
		return `${nocworxRemoteDetails.server_details.disk_space}GB`;
	}

	if (assetType === 'bare metal') {
		if (!machine) return '';

		const { disk, disk_type: diskType } = machine;

		if (!disk) return '';

		const storageParts = [];

		if (disk) {
			storageParts.push(`${disk} GB`);
		}

		if (diskType) {
			storageParts.push(diskType);
		}

		return storageParts.join(', ');
	}

	if (!featureDetails) {
		return '';
	}

	if (assetType === 'open stack') {
		const diskOption = Object.values(featureDetails).find((option) =>
			fromApiBoolean(option.capabilities.nocworxVolumeSize),
		);
		const { num_units: diskValue, description: diskDescription } =
			diskOption || {};

		return diskValue ? `${diskValue} GB` : diskDescription || '';
	}

	const disk = featureDetails.ConfigId?.disk;

	return disk ? `${disk} GB` : '';
};

const getHardDrives = ({
	featureDetails,
}: {
	featureDetails?: LWApiPublicAssetFeatureDetailsRawT;
}) => {
	if (!featureDetails) {
		return [];
	}

	const features = Object.entries(featureDetails);

	return features
		.filter(
			([key, { description }]) =>
				key.search(/HD\d/) === 0 && description !== 'No Tertiary Drive',
		)
		.map(([key, { option_key_display_text: title, ptov_id: ptovId }]) => {
			const [, childOption] =
				features.find(
					([childKey, { parent_ptov_id: parentPtovId }]) =>
						childKey.toLowerCase().includes('drivesize') &&
						parentPtovId === ptovId,
				) || [];

			const childDescription = childOption ? childOption?.description : 'None';

			return {
				title: title || key,
				description: childDescription,
			};
		});
};

const getLinkSpeed = ({
	assetType,
	machine,
}: {
	assetType: string;
	machine?: LWApiPublicAssetDetailsResultRawI['machine'];
}) => {
	if (assetType === 'bare metal') {
		if (!machine) return '';

		const { link_speed: linkSpeed } = machine;

		if (typeof linkSpeed === 'number' && linkSpeed > 0) {
			return `${linkSpeed / 1000} Gbps`;
		}

		return '';
	}

	return '';
};

const getProcessorSpeed = ({
	machine,
}: {
	machine?: LWApiPublicAssetDetailsResultRawI['machine'];
}) => {
	if (!machine) return '';

	const { cpu_speed: cpuSpeed } = machine;

	if (typeof cpuSpeed === 'number' && cpuSpeed > 0) {
		return `${cpuSpeed / 1000} GHz`;
	}

	return '';
};

const getParentDiskResources = ({
	resourceState,
}: {
	resourceState?: LWApiPublicAssetDetailsResultRawI['resourceState'];
}) => {
	if (!resourceState) return null;

	const data = resourceState.find((value) => value.type === 'disk.lvm.volume');
	if (!data) return null;

	return {
		free: Number(data.free),
		used: Number(data.used),
		total: Number(data.total),
	};
};

const getParentMemoryResources = (
	resourceState?: LWApiPublicAssetDetailsResultRawI['resourceState'],
) => {
	if (!resourceState) return null;
	return resourceState.find((value) => value.type === 'memory') || null;
};

const getBackupPlan = (
	hasAcronis: boolean,
	featureDetails?: LWApiPublicAssetFeatureDetailsRawT,
) => {
	if (hasAcronis) return 'Acronis';

	return getBackupType(featureDetails?.LiquidWebBackupPlan?.value);
};

const getBackupsState = (
	canAcronisBackup: boolean,
	canCloudBackup: boolean,
	currentBackupPlan: 'Acronis' | 'Daily' | 'CloudDaily' | 'Quota' | 'None',
	hasAcronisEdr: boolean,
) => ({
	canAcronis: canAcronisBackup,
	canCloudBackup,
	currentPlan: currentBackupPlan,
	hasAcronis: currentBackupPlan === 'Acronis',
	hasAcronisEdr,
	hasBackups: currentBackupPlan !== 'None',
	hasLegacyBackups:
		currentBackupPlan === 'Daily' ||
		currentBackupPlan === 'CloudDaily' ||
		currentBackupPlan === 'Quota',
});

const getPath = ({
	isBeyondHosting,
	isNutanixPrivateCloud: isNutanixPrivateCloudAsset,
	isPrivateParent,
	isVmware,
	uniqId,
}: {
	isBeyondHosting?: boolean;
	isNutanixPrivateCloud?: boolean;
	isPrivateParent?: boolean;
	isVmware?: boolean;
	uniqId: string;
}) => {
	if (isBeyondHosting) {
		return `/servers/${uniqId}/beyond-hosting`;
	}

	if (isPrivateParent) {
		return `/servers/${uniqId}/private-parent`;
	}

	if (isVmware) {
		return `/servers/${uniqId}/vmware-vdc`;
	}

	if (isNutanixPrivateCloudAsset) {
		return `/servers/${uniqId}/nutanix`;
	}

	return `/servers/${uniqId}`;
};

export const supplementDetails = (
	data: AssetDetailsResultRaw,
): AssetDetails => {
	const {
		productDisplayGroup,
		backupsAndStorage,
		capabilities,
		categories,
		featureDetails,
		machine,
		mesDetails,
		networkSummary,
		nocworxRemoteDetails,
		powerStatus: powerStatusRaw,
		privateParent,
		region_id: regionId,
		resourceState,
		template,
		type,
		zone,
	} = data || {};
	const { uniq_id: acronisId, status: acronisStatus } =
		backupsAndStorage?.acronis || {};
	const { uniq_id: acronisEdrId, status: acronisEdrStatus } =
		backupsAndStorage?.acronis_edr || {};

	const assetType = createAssetType(categories || []);
	const availabilityZoneId = zone?.name ? Number(zone.id) : null;
	const backupQuotaValue = featureDetails?.BackupQuota?.value;
	const bandwidthInGB = Number(featureDetails?.Bandwidth?.value.split('.')[1]);
	const canAcronisBackup = fromApiBoolean(capabilities?.canAcronisBackup);
	const canCloudBackup = fromApiBoolean(capabilities?.canCloudBackup);
	const canImage = fromApiBoolean(capabilities?.image);
	const canConsole =
		fromApiBoolean(capabilities?.canUseSpiceConsole) ||
		fromApiBoolean(capabilities?.canUseVNCConsole);
	const controlPanel =
		template?.control_panel ||
		featureDetails?.Template?.extra_software ||
		featureDetails?.ControlPanel?.description ||
		featureDetails?.CloudManagement?.description;
	const country = regionId ? dataCenterMap[regionId]?.country : undefined;
	const cpanelLicense = fromApiBoolean(capabilities?.cpanelLogin)
		? featureDetails?.cPanelLicenseTier?.description
		: undefined;
	const disableConsole = isUnknown(powerStatusRaw);
	const hardDrives = getHardDrives({
		featureDetails,
	});
	const hasAcronis = Boolean(
		backupsAndStorage && 'acronis' in backupsAndStorage,
	);
	const hasBlockStorage = Boolean(backupsAndStorage?.block_storage?.length);
	const isBareMetal = categories?.includes('BareMetalMAAS');
	const isBeyondHosting = categories?.includes('BeyondHostingVPS');
	const isChild = Boolean(privateParent);
	const isMWPv1 = type === 'WP.VM';
	const isNutanixPrivateCloudAsset = isNutanixPrivateCloud(type);
	const isNutanixVmAsset = isNutanixVm(type);
	const isPrismManagedAsset = isPrismManaged(type, categories);
	const isOpenStack = categories?.includes('NocworxOpenstack');
	const isPrivateParent = type === 'SS.PP';
	const isStorm = getIsStorm(type);
	const isVmware = type === 'VMware.vCloud.vDC';
	const location = regionId ? dataCenterMap[regionId]?.label : undefined;
	const managementLevel =
		template?.management ||
		featureDetails?.Template?.support_level ||
		featureDetails?.ControlPanel?.description ||
		featureDetails?.CloudManagement?.description;
	const mesRole = mesDetails?.mes_role;
	const mesType = mesDetails?.mes_type;
	const parentDiskResources = getParentDiskResources({ resourceState });
	const parentMemoryResources = getParentMemoryResources(resourceState);
	const privateIp = networkSummary?.private_ip;
	const processorSpeed = getProcessorSpeed({ machine });
	const provBackup = fromApiBoolean(capabilities?.provBackup);
	const raidLevelFormatted = `RAID${machine?.raid_level}`;
	const zoneFormatted = zone?.name
		? `${zone?.region.name} — ${zone?.name}`
		: undefined;

	const backupPlan = getBackupPlan(hasAcronis, featureDetails || {});
	const backupsState = getBackupsState(
		canAcronisBackup,
		canCloudBackup,
		backupPlan,
		getHasAcronisEdr(backupsAndStorage),
	);
	const canBackups = canAcronisBackup || provBackup;
	const cores = getCores({
		featureDetails,
		isPrivateParent,
		nocworxRemoteDetails,
		machine,
	});
	const cpu = getCpu({
		assetType,
		featureDetails,
		isBeyondHosting,
		isChild,
		isPrivateParent,
		isVmware,
		machine,
	});
	const displayType = productDisplayGroup || displayName(assetType, type);
	const gpu = getGpu({
		assetType,
		machine,
	});
	const linkSpeed = getLinkSpeed({
		assetType,
		machine,
	});
	const os =
		template?.os_name ||
		getOS({
			assetType,
			featureDetails,
			nocworxRemoteDetails,
			isBeyondHosting,
			machine,
		});
	const path = getPath({
		isBeyondHosting,
		isNutanixPrivateCloud: isNutanixPrivateCloudAsset,
		isPrivateParent,
		isVmware,
		uniqId: data.uniq_id,
	});
	const powerStatus = isBeyondHosting
		? getBeyondHostingPowerStatus(nocworxRemoteDetails?.power_status || '')
		: powerStatusRaw;
	const primaryStorage = getPrimaryStorage({
		assetType,
		featureDetails,
		isBeyondHosting,
		nocworxRemoteDetails,
		machine,
	});
	const processor = getProcessor({
		assetType,
		featureDetails,
		machine,
	});
	const ram = getRam({
		assetType,
		featureDetails,
		isBeyondHosting,
		isChild,
		isPrivateParent,
		machine,
	});

	const isWindows = os.toLowerCase().includes('windows');
	const osFormatted = template
		? `${stripOsName(template.os_name || '')} ${template.os_version}, ${template.os_bit}-bit`
		: os;
	const ramFormatted = getRamFormatted(ram);

	return {
		...data,
		acronisId,
		acronisStatus,
		acronisEdrId,
		acronisEdrStatus,
		assetType,
		availabilityZoneId,
		backupPlan,
		backupQuotaValue,
		backupsState,
		bandwidthInGB,
		canBackups,
		canConsole,
		canImage,
		controlPanel,
		cores,
		country,
		cpanelLicense,
		cpu,
		disableConsole,
		displayType,
		gpu,
		hardDrives,
		hasAcronis,
		hasBlockStorage,
		isBareMetal,
		isBeyondHosting,
		isMWPv1,
		isNutanixPrivateCloud: isNutanixPrivateCloudAsset,
		isNutanixVm: isNutanixVmAsset,
		isOpenStack,
		isPrismManaged: isPrismManagedAsset,
		isPrivateParent,
		isStorm,
		isVmware,
		isWindows,
		linkSpeed,
		location,
		managementLevel,
		mesRole,
		mesType,
		os,
		osFormatted,
		parentDiskResources,
		parentMemoryResources,
		path,
		powerStatus,
		primaryStorage,
		privateIp,
		processor,
		processorSpeed,
		raidLevelFormatted,
		ram,
		ramFormatted,
		zoneFormatted,
	};
};

export const ASSET_DETAILS_QUERY_KEY = '/asset/details';

const useAssetDetails = (
	params: LWApiPublicAssetDetailsParamsRawI,
	options?: UseQueryOptions<
		LWApiPublicAssetDetailsResultRawI,
		ApiErrorI,
		AssetDetails
	>,
) =>
	useQuery({
		queryKey: [ASSET_DETAILS_QUERY_KEY, params],
		queryFn: () => api.asset.apiRaw.details(params),
		...options,
		select: supplementDetails,
	});

export default useAssetDetails;
